import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  changeFieldVisibility,
  deleteTaskMetaField,
  setSelectedTaskColumns,
} from "src/redux";
import { SelectedTasksBar } from "src/app/components/SelectedTasksBar/SelectedTasksBar";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as RubberIcon } from "src/images/rubber.svg";
import { ReactComponent as DeleteIcon } from "src/images/trash-can.svg";
import { ReactComponent as MoveToTheLeftIcon } from "src/images/move-to-the-left.svg";
import { ReactComponent as MoveToTheRightIcon } from "src/images/move-to-the-right.svg";
import { ReactComponent as HideIcon } from "src/images/hide.svg";
import Loader from "src/app/components/Loader/Loader";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { RootState } from "src/redux/reducers";
import { ProjectPermissionsEnum } from "src/utils/PermissionsEnums";
import ContentProposalDownloadModal from "src/app/modals/ContentDownloadModal/ContentProposalDownloadModal";
import { saveToLocalStorage } from "src/utils/localStorage";

interface Props {
  moveColumnsToTheLeftEnd: () => void;
  moveColumnsToTheRightEnd: () => void;
}

export function SingleProjectSelectedColumnsBar(props: Props) {
  const { moveColumnsToTheLeftEnd, moveColumnsToTheRightEnd } = props;

  const {
    taskReducer: { tasksLoading, selectedTaskColumns },
    projectReducer: { projectBasicData },
  } = useSelector((state: RootState) => state);

  const [isTooltipOpen, setIsTooltipOpen] = useState(
    window.localStorage.getItem("selectedTasksBarTooltipHidden") === null
      ? true
      : !JSON.parse(
          window.localStorage.getItem(
            "selectedTasksBarTooltipHidden",
          ) as string,
        ),
  );

  const [showDeleteColumnsModal, setShowDeleteColumnsModal] = useState(false);
  const [showContentDownloadModal, setShowContentDownloadModal] =
    useState(false);

  const dispatch = useDispatch();

  const clearSelected = () => {
    dispatch(setSelectedTaskColumns([]));
  };

  const hideSelectedColumns = () => {
    const uuids = selectedTaskColumns?.map((task) => task?.metaFieldId);
    dispatch(changeFieldVisibility(uuids, false));
    dispatch(setSelectedTaskColumns([]));
  };

  const removeSelectedColumns = () => {
    const uuids = selectedTaskColumns?.map((task) => task?.metaFieldId);
    dispatch(deleteTaskMetaField(uuids));
    dispatch(setSelectedTaskColumns([]));
  };

  const hideTooltip = () => {
    saveToLocalStorage("selectedTasksBarTooltipHidden", true);
    setIsTooltipOpen(false);
  };

  const renderSelectedColumnsBarContent = () => {
    return (
      <>
        <Button
          size="large"
          variant="white"
          onClick={hideSelectedColumns}
          data-action="hide"
        >
          <HideIcon />
          <IDHFormattedMessage id="ws_hide" defaultMessage="Hide" />
        </Button>

        <Button
          size="large"
          variant="white"
          onClick={moveColumnsToTheLeftEnd}
          data-action="move-to-the-left-end"
        >
          <MoveToTheLeftIcon />
          <IDHFormattedMessage
            id="ws_move_to_the_left_end"
            defaultMessage="Move to the left end"
          />
        </Button>

        <Button
          size="large"
          variant="white"
          onClick={moveColumnsToTheRightEnd}
          data-action="move-to-the-right-end"
        >
          <MoveToTheRightIcon />
          <IDHFormattedMessage
            id="ws_move_to_the_right_end"
            defaultMessage="Move to the right end"
          />
        </Button>

        {/* Clear Selected Button */}
        <Button
          size="large"
          variant="white"
          onClick={clearSelected}
          disabled={!selectedTaskColumns.length}
          data-action="clearSelected"
        >
          <RubberIcon />
          <IDHFormattedMessage id="ws_clear_selected" defaultMessage="Clear" />
        </Button>

        {tasksLoading ? (
          <Loader />
        ) : (
          projectBasicData?.permissions?.project.includes(
            ProjectPermissionsEnum.TASK_MANAGEMENT,
          ) && (
            <Button
              size="large"
              variant="red-text"
              onClick={() => setShowDeleteColumnsModal(true)}
              disabled={!selectedTaskColumns.length}
              data-action="removeSelected"
            >
              <DeleteIcon />
              <IDHFormattedMessage id="ws_remove" defaultMessage="Remove" />
            </Button>
          )
        )}

        {/* Confirmation and Sharing Modals */}
        {showDeleteColumnsModal && (
          <RemoveModal
            onClose={() => setShowDeleteColumnsModal(false)}
            objectNames={
              selectedTaskColumns?.map((task) => task?.metaFieldName) ?? []
            }
            removeFunction={() => {
              removeSelectedColumns();
              setShowDeleteColumnsModal(false);
            }}
          />
        )}

        {showContentDownloadModal && (
          <ContentProposalDownloadModal
            onClose={() => {
              setShowContentDownloadModal(false);
            }}
          />
        )}
      </>
    );
  };

  return (
    <SelectedTasksBar
      selectedTasksLength={selectedTaskColumns.length}
      isTooltipOpen={isTooltipOpen}
      hideTooltip={hideTooltip}
    >
      {renderSelectedColumnsBarContent()}
    </SelectedTasksBar>
  );
}
